package com.xtream.data_kmp.db.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import com.xtream.data_kmp.WatchedSeriesStreams
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class WatchedSeriesDao_Impl(
  __db: RoomDatabase,
) : WatchedSeriesDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfWatchedSeriesStreams: EntityInsertAdapter<WatchedSeriesStreams>

  private val __deleteAdapterOfWatchedSeriesStreams:
      EntityDeleteOrUpdateAdapter<WatchedSeriesStreams>
  init {
    this.__db = __db
    this.__insertAdapterOfWatchedSeriesStreams = object : EntityInsertAdapter<WatchedSeriesStreams>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `watchedSeriesStreamsTable` (`episodeId`,`videoExtension`,`streamId`,`progress`,`totalProgress`,`backdrop`,`added`) VALUES (?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: WatchedSeriesStreams) {
        statement.bindLong(1, entity.episodeId.toLong())
        val _tmpVideoExtension: String? = entity.videoExtension
        if (_tmpVideoExtension == null) {
          statement.bindNull(2)
        } else {
          statement.bindText(2, _tmpVideoExtension)
        }
        statement.bindLong(3, entity.streamId.toLong())
        val _tmpProgress: Long? = entity.progress
        if (_tmpProgress == null) {
          statement.bindNull(4)
        } else {
          statement.bindLong(4, _tmpProgress)
        }
        val _tmpTotalProgress: Long? = entity.totalProgress
        if (_tmpTotalProgress == null) {
          statement.bindNull(5)
        } else {
          statement.bindLong(5, _tmpTotalProgress)
        }
        val _tmpBackdrop: String? = entity.backdrop
        if (_tmpBackdrop == null) {
          statement.bindNull(6)
        } else {
          statement.bindText(6, _tmpBackdrop)
        }
        statement.bindLong(7, entity.added)
      }
    }
    this.__deleteAdapterOfWatchedSeriesStreams = object : EntityDeleteOrUpdateAdapter<WatchedSeriesStreams>() {
      protected override fun createQuery(): String = "DELETE FROM `watchedSeriesStreamsTable` WHERE `streamId` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: WatchedSeriesStreams) {
        statement.bindLong(1, entity.streamId.toLong())
      }
    }
  }

  public override suspend fun insertAll(streams: List<WatchedSeriesStreams>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfWatchedSeriesStreams.insert(_connection, streams)
  }

  public override suspend fun insert(watchedStream: WatchedSeriesStreams): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfWatchedSeriesStreams.insert(_connection, watchedStream)
  }

  public override suspend fun delete(watched: WatchedSeriesStreams): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfWatchedSeriesStreams.handle(_connection, watched)
  }

  public override suspend fun deleteAll(watchedList: List<WatchedSeriesStreams>): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfWatchedSeriesStreams.handleMultiple(_connection, watchedList)
  }

  public override suspend fun getSingle(streamId: Int?): WatchedSeriesStreams? {
    val _sql: String = "SELECT * FROM watchedSeriesStreamsTable WHERE (streamId = ?) LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (streamId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, streamId.toLong())
        }
        val _columnIndexOfEpisodeId: Int = getColumnIndexOrThrow(_stmt, "episodeId")
        val _columnIndexOfVideoExtension: Int = getColumnIndexOrThrow(_stmt, "videoExtension")
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfProgress: Int = getColumnIndexOrThrow(_stmt, "progress")
        val _columnIndexOfTotalProgress: Int = getColumnIndexOrThrow(_stmt, "totalProgress")
        val _columnIndexOfBackdrop: Int = getColumnIndexOrThrow(_stmt, "backdrop")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _result: WatchedSeriesStreams?
        if (_stmt.step()) {
          val _tmpEpisodeId: Int
          _tmpEpisodeId = _stmt.getLong(_columnIndexOfEpisodeId).toInt()
          val _tmpVideoExtension: String?
          if (_stmt.isNull(_columnIndexOfVideoExtension)) {
            _tmpVideoExtension = null
          } else {
            _tmpVideoExtension = _stmt.getText(_columnIndexOfVideoExtension)
          }
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpProgress: Long?
          if (_stmt.isNull(_columnIndexOfProgress)) {
            _tmpProgress = null
          } else {
            _tmpProgress = _stmt.getLong(_columnIndexOfProgress)
          }
          val _tmpTotalProgress: Long?
          if (_stmt.isNull(_columnIndexOfTotalProgress)) {
            _tmpTotalProgress = null
          } else {
            _tmpTotalProgress = _stmt.getLong(_columnIndexOfTotalProgress)
          }
          val _tmpBackdrop: String?
          if (_stmt.isNull(_columnIndexOfBackdrop)) {
            _tmpBackdrop = null
          } else {
            _tmpBackdrop = _stmt.getText(_columnIndexOfBackdrop)
          }
          _result = WatchedSeriesStreams(_tmpStreamId,_tmpEpisodeId,_tmpProgress,_tmpVideoExtension,_tmpTotalProgress,_tmpBackdrop)
          _result.added = _stmt.getLong(_columnIndexOfAdded)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getSingleFlow(streamId: Int?): Flow<WatchedSeriesStreams?> {
    val _sql: String = "SELECT * FROM watchedSeriesStreamsTable WHERE (episodeId = ?)ORDER BY added DESC LIMIT 1"
    return createFlow(__db, false, arrayOf("watchedSeriesStreamsTable")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (streamId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, streamId.toLong())
        }
        val _columnIndexOfEpisodeId: Int = getColumnIndexOrThrow(_stmt, "episodeId")
        val _columnIndexOfVideoExtension: Int = getColumnIndexOrThrow(_stmt, "videoExtension")
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfProgress: Int = getColumnIndexOrThrow(_stmt, "progress")
        val _columnIndexOfTotalProgress: Int = getColumnIndexOrThrow(_stmt, "totalProgress")
        val _columnIndexOfBackdrop: Int = getColumnIndexOrThrow(_stmt, "backdrop")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _result: WatchedSeriesStreams?
        if (_stmt.step()) {
          val _tmpEpisodeId: Int
          _tmpEpisodeId = _stmt.getLong(_columnIndexOfEpisodeId).toInt()
          val _tmpVideoExtension: String?
          if (_stmt.isNull(_columnIndexOfVideoExtension)) {
            _tmpVideoExtension = null
          } else {
            _tmpVideoExtension = _stmt.getText(_columnIndexOfVideoExtension)
          }
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpProgress: Long?
          if (_stmt.isNull(_columnIndexOfProgress)) {
            _tmpProgress = null
          } else {
            _tmpProgress = _stmt.getLong(_columnIndexOfProgress)
          }
          val _tmpTotalProgress: Long?
          if (_stmt.isNull(_columnIndexOfTotalProgress)) {
            _tmpTotalProgress = null
          } else {
            _tmpTotalProgress = _stmt.getLong(_columnIndexOfTotalProgress)
          }
          val _tmpBackdrop: String?
          if (_stmt.isNull(_columnIndexOfBackdrop)) {
            _tmpBackdrop = null
          } else {
            _tmpBackdrop = _stmt.getText(_columnIndexOfBackdrop)
          }
          _result = WatchedSeriesStreams(_tmpStreamId,_tmpEpisodeId,_tmpProgress,_tmpVideoExtension,_tmpTotalProgress,_tmpBackdrop)
          _result.added = _stmt.getLong(_columnIndexOfAdded)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getSize(): Long? {
    val _sql: String = "SELECT COUNT(*) FROM watchedSeriesStreamsTable"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteById(episodeId: Int?) {
    val _sql: String = "DELETE FROM watchedSeriesStreamsTable WHERE episodeId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (episodeId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, episodeId.toLong())
        }
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteWatchedStreamById(streamId: Int) {
    val _sql: String = "DELETE FROM watchedSeriesStreamsTable WHERE streamId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, streamId.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM watchedSeriesStreamsTable"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteByOffset(offset: Int, limit: Int) {
    val _sql: String = """
        |
        |       DELETE FROM watchedSeriesStreamsTable
        |        WHERE streamId in 
        |(SELECT streamId FROM watchedSeriesStreamsTable ORDER BY added DESC  LIMIT ?  OFFSET ?)
        |   
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 2
        _stmt.bindLong(_argIndex, offset.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
